import { Store } from '@ngrx/store';
import { ReplaySubject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { coreSelectors } from 'app/core/state';
import SocialStateHelper from 'app/modules/social/social-state-machine/social-state-helper';
import SocialStateMachine from 'app/modules/social/social-state-machine/social-state-machine';
import { mediaSelectors } from 'app/modules/social/state/media';
import { AppState, Interval, Timeout, WindowWithWebkitAudio } from 'app/types';
import assertDefined from 'app/utils/assert-defined';
import { Loggable } from 'app/utils/logging/loggable';
import { cleanTracks, getUserMedia } from 'app/utils/media/stream';
import User from 'entity/User';

import { DeviceService } from '../../device/device.service';
import { BlackSimpleToasterService } from '../../toasters/black-simple-toaster.service';

const arrayAverage = (array: number[]) => array.reduce((a, b) => a + b) / array.length;

// Run connection checker every N seconds
const MONITOR_INTERVAL_SECONDS = 0.25;
const NOTIFICATION_DISMISS_TIMER_SECONDS = 7;
const AVERAGE_STACK_LENGTH = 20;
const DEFAULT_AVERAGE_STACK = [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0];

export default class MuteListener extends Loggable {
  private user?: User;

  // Derived from social state
  private isSpeakingUser = true;
  private allowGroupConversations = true;

  // Monitor Intervals
  private monitorInterval?: Interval;
  // Sound
  private audioContext?: AudioContext;
  private soundAnalyser?: AnalyserNode;
  private dataArray?: Uint8Array;
  private averageStack = [...DEFAULT_AVERAGE_STACK];

  private notificationInProgress = false;
  private notificationDismissed = false;
  private notificationDismissedAutomaticallyTimeout?: Timeout;

  private muteCheckStream?: MediaStream;
  private destroyed$ = new ReplaySubject<boolean>(1);
  private isMicEnabled = true;

  public constructor(
    private toastNotificationService: BlackSimpleToasterService,
    private deviceService: DeviceService,
    private store: Store<AppState>,
  ) {
    super();
    this.setDisplayName('MuteListener');
    this.deviceService.audioStream$
      .pipe(takeUntil(this.destroyed$))
      .subscribe((stream) => this.createAudioStream(stream));

    this.store
      .select(coreSelectors.getUserEntity)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((user) => (this.user = user));
    this.store
      .select(mediaSelectors.isMicEnabled)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((enabled) => (this.isMicEnabled = enabled));

    SocialStateMachine.shared.stateChanged$.pipe(takeUntil(this.destroyed$)).subscribe((state) => {
      assertDefined(this.user?.userId);
      const { userId } = this.user;
      const { allowGroupConversations } = SocialStateHelper.getEventInfo(state);

      this.allowGroupConversations = allowGroupConversations;
      this.isSpeakingUser = SocialStateHelper.isSpeakerOrPresenter({ state, userId });
    });

    // Initiate audioContext, soundAnalyser and dataArray
    window.AudioContext = window.AudioContext || (window as WindowWithWebkitAudio).webkitAudioContext;

    // Initiate soundChecker to run constantly
    this.monitorInterval = setInterval(() => this.soundChecker(), MONITOR_INTERVAL_SECONDS * 1000);
  }

  public async destroy(): Promise<void> {
    this.info('Destroying mute listener');

    await cleanTracks(this.muteCheckStream, this);
    this.muteCheckStream = undefined;

    if (this.monitorInterval) {
      clearInterval(this.monitorInterval);
    }
    this.monitorInterval = undefined;

    const { audioContext } = this;
    this.audioContext = undefined;
    try {
      if (audioContext?.state !== 'closed') {
        await audioContext?.close();
      }
    } catch (err) {
      this.error('Error Closing Audio Context', err);
    }

    this.averageStack = [...DEFAULT_AVERAGE_STACK];
    this.soundAnalyser = undefined;
    this.dataArray = undefined;
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }

  /**
   * Create a new mute check audio stream based on the selected audio stream
   *
   * Close our current audio context, and rebuild the new audio analyser based on what the user has selected, if anything.
   *
   * @param newStream The new media stream to mirror
   */
  private async createAudioStream(newStream: MediaStream | undefined): Promise<void> {
    const { audioContext } = this;
    this.audioContext = undefined;
    try {
      await audioContext?.close();
    } catch (err) {
      this.error('Error Closing Audio Context', err);
    }

    await cleanTracks(this.muteCheckStream, this);
    this.muteCheckStream = undefined;

    if (!newStream) {
      return;
    }

    const constraints: MediaStreamConstraints = { audio: { deviceId: { exact: this.deviceService.mic?.deviceId } } };

    try {
      this.muteCheckStream = await getUserMedia(constraints, this);
    } catch (e) {
      this.error('Error getting user media', e);
    }

    if (!this.muteCheckStream) {
      this.error('Error getting user media, muteCheckStream is undefined');
      return;
    }

    // Create and connect the new analyser instance to the new stream
    this.audioContext = new AudioContext();
    this.soundAnalyser = this.audioContext.createAnalyser();
    this.soundAnalyser.fftSize = 32;
    this.dataArray = new Uint8Array(this.soundAnalyser.frequencyBinCount);
    this.audioContext.createMediaStreamSource(this.muteCheckStream).connect(this.soundAnalyser);
  }

  private soundChecker(): void {
    // don't run if no audio context
    if (!this.audioContext) {
      return;
    }

    // don't run if mic is enable but reset the notification flag
    if (this.isMicEnabled) {
      if (this.notificationDismissedAutomaticallyTimeout) {
        clearTimeout(this.notificationDismissedAutomaticallyTimeout);
      }
      this.notificationDismissed = false;
      this.closeNotification();
      this.averageStack = [...DEFAULT_AVERAGE_STACK];
      return;
    }

    if (this.dataArray === undefined) {
      return;
    }

    // Calculate average of this run
    this.soundAnalyser?.getByteTimeDomainData(this.dataArray);
    const diffSum = this.dataArray.reduce((acc: number, value: number): number => acc + Math.abs(128 - value), 0);
    const diffAvg = diffSum / this.dataArray.length;

    // Calculate average of last N runs
    this.averageStack.unshift(diffAvg);
    this.averageStack.length = AVERAGE_STACK_LENGTH;
    const stackDiffAvg = arrayAverage(this.averageStack);

    // Throw Notification
    if (stackDiffAvg > 2) {
      this.showNotification();
    }
  }

  private showNotification(): void {
    // Validation
    if (this.notificationInProgress || this.notificationDismissed) {
      return;
    }

    // Don't show banner if the user isn't allowed to speak
    if (!this.allowGroupConversations && !this.isSpeakingUser) {
      return;
    }

    this.notificationInProgress = true;
    this.toastNotificationService.throw(
      'You are muted. Click on the microphone button below or your spacebar to unmute yourself',
      'Dismiss',
      () => {
        if (this.notificationDismissedAutomaticallyTimeout) {
          clearTimeout(this.notificationDismissedAutomaticallyTimeout);
        }
        this.notificationDismissed = true;
        this.closeNotification();
      },
    );

    // Dismiss notification automatically after N Seconds
    this.notificationDismissedAutomaticallyTimeout = setTimeout(() => {
      this.notificationDismissed = true;
      this.closeNotification();
    }, NOTIFICATION_DISMISS_TIMER_SECONDS * 1000);
  }

  private closeNotification(): void {
    if (!this.notificationInProgress) {
      return;
    }

    this.notificationInProgress = false;
    this.toastNotificationService.close();
  }
}
